import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilState } from "recoil"
import ClockSVG from "src/assets/icons/clock.svg"
import EventTypeSVG from "src/assets/icons/event_type.svg"
import LocationSVG from "src/assets/icons/location.svg"
import { useLoginContext } from "src/hooks/useLoginContext"
import {
  APPLICATION_STATUS,
  EVENT_STATUS,
  EVENT_TYPE,
  MEMBER_TYPE_STRING_CAPITAL,
  RESPONSE_CODE,
  SNACKBAR_TYPES,
} from "src/modules/const"
import { getLabel } from "src/modules/helper"
import { Event, FavoriteService } from "src/openapi"
import { ShowProgress } from "src/states/showProgress"
import { ShowToast } from "src/states/showToast"
import { Box, Image, Typography } from "src/UILibrary"
import "./event_item.scss"

type Prop = {
  item: Event
  handleSelected: any
  showFavorite?: boolean
  clickable?: boolean
  showBehalfJoinCnt?: boolean
}

export const EventItem = ({
  item,
  handleSelected,
  showFavorite,
  clickable,
  showBehalfJoinCnt,
}: Prop) => {
  const { t } = useTranslation("translation", { keyPrefix: "event" })

  const { areas, accessToken, checkLoginStatus } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [favorite, setFavorite] = useState<number>(item.isFavorite as number)

  const handleFavorite = () => {
    if (favorite) {
      setShowProgress(true)
      FavoriteService.deleteFavorite(accessToken, favorite).then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_unfavorite"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          setFavorite(0)
        } else {
          setShowToast({ show: true, text: t("failed_unfavorite"), type: SNACKBAR_TYPES.ERROR })
        }
      })
    } else {
      setShowProgress(true)
      FavoriteService.createFavorite(accessToken, { eventId: item.id as number }).then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_favorite"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          setFavorite(res.data?.id as number)
        } else {
          setShowToast({ show: true, text: t("failed_favorite"), type: SNACKBAR_TYPES.ERROR })
        }
      })
    }
  }

  const getApplicationCount = () => {
    if (!item.eventApplications) {
      return 0
    }

    let count = 0
    for (const application of item.eventApplications) {
      if (application.application?.status === APPLICATION_STATUS.RESERVED) {
        count++
      }
    }
    return count
  }

  const getBehalfJointCnt = () => {
    if (!item || !item.eventApplications) {
      return 0
    }

    let count = 0
    for (const application of item.eventApplications) {
      if (application.application?.userType === MEMBER_TYPE_STRING_CAPITAL.ADMINISTRATOR) {
        count++
      }
    }

    return count
  }

  return (
    <Box
      width="100%"
      className="event-item-container"
      sx={clickable ? { ":hover": { opacity: 0.8, cursor: "pointer" } } : {}}
      onClick={() => handleSelected(item)}
    >
      <Image
        src={item.imageUrl as string}
        sx={{ bgcolor: "divider" }}
        className="event-item-img"
        alt=""
        imageStyle={{ width: "100%", height: "100%" }}
      />
      {item.status === EVENT_STATUS.STARTED && (
        <Box sx={{ px: "10px", py: "6px", bgcolor: "#FFB800", position: "absolute", top: "0px" }}>
          <Typography.ExtraSmallText sx={{ color: "white" }}>
            {t("started")}
          </Typography.ExtraSmallText>
        </Box>
      )}
      {item.status === EVENT_STATUS.RECEIVING && (
        <Box sx={{ px: "10px", py: "6px", bgcolor: "#ED8C81", position: "absolute", top: "0px" }}>
          <Typography.ExtraSmallText sx={{ color: "white" }}>
            {t("receiving")}
          </Typography.ExtraSmallText>
        </Box>
      )}
      <Box className="event-content-container" display="flex" sx={{ p: "20px" }}>
        <Box
          sx={{
            my: "auto",
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Typography.Large
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            className="font-large"
          >
            {item.title}
          </Typography.Large>
          {showFavorite && item.status === EVENT_STATUS.ARCHIVED && (
            <>
              {favorite === 0 ? (
                <Box
                  sx={{
                    ml: "auto",
                    mt: "10px",
                    bgcolor: "#7DC522",
                    px: "10px",
                    py: "6px",
                    borderRadius: "40px",
                    ":hover": { opacity: 0.8, cursor: "pointer" },
                  }}
                  onClick={() => handleFavorite()}
                >
                  <Typography.ExtraSmallText
                    sx={{ color: "white", mx: "auto", my: "auto" }}
                    id="favorite_action"
                  >
                    {t("favorite")}
                  </Typography.ExtraSmallText>
                </Box>
              ) : (
                <Box
                  sx={{
                    ml: "auto",
                    mt: "10px",
                    bgcolor: "#D32F2F",
                    px: "10px",
                    py: "6px",
                    borderRadius: "40px",
                    ":hover": { opacity: 0.8, cursor: "pointer" },
                  }}
                  onClick={() => handleFavorite()}
                >
                  <Typography.ExtraSmallText
                    sx={{ color: "white", mx: "auto", my: "auto" }}
                    id="favorite_action"
                  >
                    {t("unfavorite")}
                  </Typography.ExtraSmallText>
                </Box>
              )}
            </>
          )}

          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              mt: "10px",
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
          >
            {item.description}
          </Typography.DetailText>
          <Box display="flex" sx={{ mt: "20px" }}>
            <Image src={ClockSVG} sx={{ my: "auto", lineHeight: "16px" }} />
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", ml: "5px", my: "auto" }}
            >
              {item.startTime?.slice(0, 16)}-{item.finishTime?.slice(11, 16)}
            </Typography.DetailText>
          </Box>
          <Box display="flex">
            <Image src={LocationSVG} sx={{ my: "auto", lineHeight: "16px" }} />
            <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", ml: "5px" }}>
              {getLabel(areas, item.areaId as number)}
            </Typography.DetailText>
          </Box>
          <Box display="flex">
            <Image src={EventTypeSVG} sx={{ my: "auto", lineHeight: "16px" }} />
            <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", ml: "10px" }}>
              {item.type === EVENT_TYPE.ONLINE
                ? t("zoom_open")
                : item.type === EVENT_TYPE.OFFLINE
                ? t("offline")
                : `${t("online")}/${t("zoom_open")}`}
            </Typography.DetailText>
          </Box>
          <Box display="flex">
            <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important" }}>
              {`定員 ${getApplicationCount()}名/${item.applicationMax}名${
                showBehalfJoinCnt ? t("behalf_join_cnt", { cnt: getBehalfJointCnt() }) : ""
              }`}
            </Typography.DetailText>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
